const _ = require("lodash");
const moment = require("moment");
const {atUser, atPage} = require("./StateCursors");
const TrainerCore = require("../../../shared/TrainerCore");
const OpsPage = require("./OpsPage");
const OpsUser = require("./OpsUser");
const Bundles = require("../Bundles");
const Constants = require("../../../shared/Constants");
const {getFeatureFlag} = require("../util/FeatureFlags");
const {
	BEASTBUILDER_STEPS,
	getBeastbuilderContinueStep,
} = require("../util/TriggersUtil");

const triggerStudentNotifications = function(
	vault,
	oldState,
	newState,
	hasPageDataUpdates
) {
	if (!canTrigger(newState)) return {newState, hasPageDataUpdates};
	let pageUpdates = hasPageDataUpdates;
	const user = atUser(newState).get();
	const userModel = _.get(user, "model");
	const lastLevelNotified = userModel.lastLevelNotified;
	const userCurrentLevel = TrainerCore.getLevel(userModel.xp);
	const page = atPage(newState).get();

	const notifications = atUser(newState).getIn([
		"model",
		"eventsTriggered",
		"notifications",
	]);

	const newNotif = _.cloneDeep(notifications);

	if (shouldTriggerLevel(userCurrentLevel, lastLevelNotified)) {
		vault.emitEvent("showLevelUpModal", userCurrentLevel, lastLevelNotified);
		newState = atUser(newState).setIn(
			["model", "lastLevelNotified"],
			userCurrentLevel
		);
		newState = atPage(newState).setIn("isShowingNotification", true);
		pageUpdates = true;
	} else if (shouldTriggerTrainerRedesign(page, userModel, newNotif)) {
		vault.emitEvent("showNotificationModal", "redesignTrainer");
		newState = atUser(newState).setIn(
			["model", "eventsTriggered", "notifications"],
			_.extend(newNotif, {redesign: "shown"})
		);
		newState = atPage(newState).setIn("isShowingNotification", true);
		pageUpdates = true;
	} else if (shouldTriggerTrophyModal(page, user, newNotif)) {
		vault.emitEvent("showNotificationModal", "trophyUnlocked");
		newState = atUser(newState).setIn(
			["model", "eventsTriggered", "notifications"],
			_.extend(newNotif, {trophyUnlocked: "shown"})
		);
		newState = atPage(newState).setIn("isShowingNotification", true);
		pageUpdates = true;
	} else if (shouldTriggerTrainerLeaderboards(user, newNotif)) {
		vault.emitEvent("showNotificationModal", "leaderboardsTrainer");
		newState = atUser(newState).setIn(
			["model", "eventsTriggered", "notifications"],
			_.extend(newNotif, {leaderboards: "shown"})
		);
		newState = atPage(newState).setIn("isShowingNotification", true);
		pageUpdates = true;
	}

	// Triggers should also include a skip (level notification is the exception)
	if (shouldSkipTrainerRedesign(page, userModel, newNotif)) {
		newState = atUser(newState).setIn(
			["model", "eventsTriggered", "notifications"],
			_.extend({redesign: "skipped"}, newNotif)
		);
		pageUpdates = true;
	}
	if (shouldSkipTrophyModal(user, newNotif)) {
		newState = atUser(newState).setIn(
			["model", "eventsTriggered", "notifications"],
			_.extend({trophyUnlocked: "skipped"}, newNotif)
		);
		pageUpdates = true;
	}
	if (shouldSkipTrainerLeaderboards(user, newNotif)) {
		newState = atUser(newState).setIn(
			["model", "eventsTriggered", "notifications"],
			_.extend({leaderboards: "skipped"}, newNotif)
		);
		pageUpdates = true;
	}

	return {newState, hasPageDataUpdates: pageUpdates};
};

const triggerReportNotifications = function(
	vault,
	oldState,
	newState,
	hasPageDataUpdates
) {
	if (!canTrigger(newState, true)) return {newState, hasPageDataUpdates};

	let pageUpdates = hasPageDataUpdates;
	const user = atUser(newState).get();
	const page = atPage(newState).get();

	const notifications = atUser(newState).getIn([
		"model",
		"eventsTriggered",
		"notifications",
	]);

	if (shouldTriggerReportRedesign(page, user, notifications)) {
		vault.emitEvent("showOfficeModal", "redesignNotification");
		newState = atUser(newState).setIn(
			["model", "eventsTriggered", "notifications"],
			_.extend({redesign: "shown"}, notifications)
		);
		pageUpdates = true;
	}
	if (shouldSkipReportRedesign(page, user, notifications)) {
		newState = atUser(newState).setIn(
			["model", "eventsTriggered", "notifications"],
			_.extend({redesign: "skipped"}, notifications)
		);
		pageUpdates = true;
	}
	if (shouldTriggerReportLeaderboards(page, user, notifications)) {
		vault.emitEvent("showOfficeModal", "leaderboardsNotification");
		newState = atUser(newState).setIn(
			["model", "eventsTriggered", "notifications"],
			_.extend({leaderboards: "shown"}, notifications)
		);
		pageUpdates = true;
	}
	if (shouldSkipReportLeaderboards(user, notifications)) {
		newState = atUser(newState).setIn(
			["model", "eventsTriggered", "notifications"],
			_.extend({leaderboards: "skipped"}, notifications)
		);
		pageUpdates = true;
	}

	return {newState, hasPageDataUpdates: pageUpdates};
};

const triggerTutorials = function(
	vault,
	oldState,
	newState,
	hasPageDataUpdates
) {
	const userModel = atUser(newState).getIn("model");
	if (!canTrigger(newState) || !userModel)
		return {newState, hasPageDataUpdates};
	let pageUpdates = hasPageDataUpdates;
	const page = atPage(newState).get();
	const oldPage = atPage(oldState).get();
	const hasPageChanged = oldPage.name !== page.name;

	const tutorials = atUser(newState).getIn([
		"model",
		"eventsTriggered",
		"tutorials",
	]);

	let bbStep = _.get(tutorials, "beastbuilder");
	// Skip to last step of tutorial if already started and then abandoned
	if (hasPageChanged && bbStep && oldPage.name === "beastbuilder.main") {
		bbStep = getBeastbuilderContinueStep(bbStep);
		newState = updateTutorialStep(newState, tutorials, "beastbuilder", bbStep);
		pageUpdates = true;
	} else if (shouldStartBeastbuilder(page, userModel, tutorials)) {
		if (OpsPage.isInBeastBuilder(page) && bbStep === BEASTBUILDER_STEPS[0]) {
			bbStep = BEASTBUILDER_STEPS[1];
		}
		vault.emitEvent("startBeastbuilderTutorial", bbStep);
		newState = updateTutorialStep(newState, tutorials, "beastbuilder", bbStep);
		pageUpdates = true;
	}

	return {newState, hasPageDataUpdates: pageUpdates};
};

const triggerTeacherWelcome = function(
	vault,
	oldState,
	newState,
	hasPageDataUpdates
) {
	const user = atUser(newState).get();
	const page = atPage(newState).get();
	const notifications = atUser(newState).getIn([
		"model",
		"eventsTriggered",
		"notifications",
	]);
	let pageUpdates = hasPageDataUpdates;
	if (shouldTriggerTeacherWelcome(page, user, notifications)) {
		vault.emitEvent("showTeacherWelcome");
		newState = atUser(newState).setIn(
			["model", "eventsTriggered", "notifications"],
			_.extend(notifications, {teacherWelcome: "shown"})
		);
		pageUpdates = true;
	}
	return {newState, hasPageDataUpdates: pageUpdates};
};

const getPlacementTestInvitePageKey = function(page) {
	if (!page || !page.name) {
		return "";
	}
	if (page.name === "home.main") {
		return "home.main";
	}
	if (page.name === "trainer.chapter") {
		return [
			"trainer.chapter",
			String(page.gradeNumber),
			String(page.chapterIndex),
		].join(":");
	}
	return page.name;
};

const triggerPlacementTestInviteModal = function(
	vault,
	oldState,
	newState,
	hasPageDataUpdates
) {
	const user = atUser(newState).get();
	const page = atPage(newState).get();
	const notifications = atUser(newState).getIn([
		"model",
		"eventsTriggered",
		"notifications",
	]);

	const oldPage = atPage(oldState).get();
	if (
		getPlacementTestInvitePageKey(oldPage) !==
		getPlacementTestInvitePageKey(page)
	) {
		vault._placementTestInviteEventEmitted = false;
	}

	if (
		shouldTriggerPlacementTestInviteModal(page, user, notifications, newState)
	) {
		if (!vault._placementTestInviteEventEmitted) {
			vault.emitEvent("showPlacementTestInviteModal");
			vault._placementTestInviteEventEmitted = true;
		}
	}

	return {newState, hasPageDataUpdates};
};
// Internal helpers

const updateTutorialStep = function(newState, tutorials, tutorialType, step) {
	newState = atUser(newState).setIn(
		["model", "eventsTriggered", "tutorials"],
		_.extend(
			{[tutorialType]: step || BEASTBUILDER_STEPS[0]},
			_.omit(tutorials, "beastbuilder")
		)
	);
	return newState;
};

// Was user created before the event in question
const isUserBeforeEvent = function(userCreatedAt, eventKey) {
	userCreatedAt = moment(userCreatedAt);
	const wentLiveDate = Constants[eventKey.toUpperCase() + "_WENT_LIVE"];
	return userCreatedAt.isBefore(wentLiveDate);
};

const canTrigger = function(newState, isInOffice) {
	const newPage = atPage(newState).get();
	const user = atUser(newState).get();

	const testInProgress =
		OpsPage.isCurrentlyTakingTest(user, newPage) &&
		!OpsPage.isPageForCurrentTest(user, newPage);

	const officeBundleCheck = isInOffice
		? Bundles.getStatus("office") === "done"
		: true;

	const trainerBundleCheck = !isInOffice
		? Bundles.getStatus("trainer") === "done"
		: true;

	return (
		!testInProgress &&
		!newPage.demo &&
		!newPage.isShowingNotification &&
		officeBundleCheck &&
		trainerBundleCheck
	);
};

const shouldTriggerLevel = function(userCurrentLevel, lastLevelNotified) {
	return (
		(userCurrentLevel > 1 && !lastLevelNotified) ||
		userCurrentLevel > lastLevelNotified
	);
};

const shouldTriggerTrainerRedesign = function(page, userModel, notifications) {
	const userCurrentLevel = TrainerCore.getLevel(userModel.xp);
	return userCurrentLevel > 1 && !_.get(notifications, "redesign");
};

const shouldTriggerReportRedesign = function(page, user, notifications) {
	const userCreatedAt = _.get(user, "model.createdAt");
	// Ensures the user model is loaded and we have the date when the user was created.
	if (!userCreatedAt) return false;
	// If a parent/teacher was created before the redesign went live,
	// we want to show them this modal.
	const userCreatedPreRedesign = isUserBeforeEvent(userCreatedAt, "redesign");
	const isParentOrTeacher =
		OpsUser.hasParentRole(user) || OpsUser.hasTeacherRole(user);

	return (
		userCreatedPreRedesign &&
		isParentOrTeacher &&
		!_.get(notifications, "redesign")
	);
};

const shouldTriggerTeacherWelcome = function(page, user, notifications) {
	const isTeacher = OpsUser.hasDashboard(user);
	const hasSeenWelcome = _.get(notifications, "teacherWelcome") === "shown";
	const userCreatedBeforeFix = isUserBeforeEvent(
		_.get(user, "model.createdAt"),
		"teacher_welcome_fix"
	);
	return isTeacher && !hasSeenWelcome && !userCreatedBeforeFix;
};

const shouldTriggerPlacementTestInviteModal = function(
	page,
	user,
	notifications,
	state
) {
	const isFeatureEnabled = getFeatureFlag(
		"ENABLE_PLACEMENT_TEST_INVITE_MODAL",
		false
	);

	if (!isFeatureEnabled) return false;

	// Make sure we're only showing the invite on the right pages
	// either on the trainer home page or on the chapter path
	const pathname = _.get(global, "window.location.pathname", "");
	const isSchoolLandingPath =
		pathname === "/school" || pathname.startsWith("/school/");
	const isTrainerHomePage = page && page.name === "home.main";
	const isChapterPathPage = page && page.name === "trainer.chapter";
	const isEligiblePage =
		isSchoolLandingPath && (isTrainerHomePage || isChapterPathPage);

	// early exit if the page is not eligible so we don't waste time checking the student
	if (!isEligiblePage) return false;

	// get the right student audience - we don't want students who are B2B / "school" students
	// but we do want academy students. the student must have taken at least one chapter test block.
	// the student must not have seen the invite already.
	const isStudent = OpsUser.isStudent(user);
	const isAcademyStudent = OpsUser.isAcademyStudent(user);
	const isSchoolStudent = OpsUser.isSchoolStudent(user);
	const hasTakenTest = OpsUser.hasTakenAnyChapterTest(state);
	const hasSeenInvite =
		_.get(notifications, "placementTestInviteModal") === "shown";
	const isEligibleStudent =
		isStudent &&
		(!isSchoolStudent || isAcademyStudent) &&
		hasTakenTest &&
		!hasSeenInvite;

	return isEligibleStudent;
};

const shouldSkipTrainerRedesign = function(page, userModel, notifications) {
	const userCurrentLevel = TrainerCore.getLevel(userModel.xp);
	return userCurrentLevel <= 1 && !_.get(notifications, "redesign");
};

const shouldSkipReportRedesign = function(page, user, notifications) {
	const userCreatedAt = _.get(user, "model.createdAt");
	// Ensures the user model is loaded and we have the date when the user was created.
	if (!userCreatedAt) return false;
	const userCreatedPreRedesign = isUserBeforeEvent(userCreatedAt, "redesign");
	const isParentOrTeacher =
		OpsUser.hasParentRole(user) || OpsUser.hasTeacherRole(user);

	return (
		!_.get(notifications, "redesign") &&
		(!userCreatedPreRedesign || !isParentOrTeacher)
	);
};

const shouldTriggerReportLeaderboards = function(page, user, notifications) {
	const userCreatedAt = _.get(user, "model.createdAt");
	// Ensures the user model is loaded and we have the date when the user was created.
	if (!userCreatedAt) return false;
	// If a parent was created before leaderboards went live,
	// we want to show them this modal.
	const userCreatedPreLeaderboards = isUserBeforeEvent(
		userCreatedAt,
		"parent_leaderboards"
	);
	// Only shows up for parents, not teachers for now.
	const isParent = OpsUser.hasParentRole(user) && !OpsUser.hasTeacherRole(user);

	return (
		userCreatedPreLeaderboards &&
		isParent &&
		!_.get(notifications, "leaderboards")
	);
};

const shouldTriggerTrainerLeaderboards = function(user, notifications) {
	const canViewLeaderboards = OpsUser.canViewLeaderboards(user);
	const userCreatedAt = _.get(user, "model.createdAt");
	// Ensures the user model is loaded and we have the date when the user was created.
	if (!userCreatedAt) return false;
	const userCreatedPreLeaderboards = isUserBeforeEvent(
		userCreatedAt,
		"parent_leaderboards"
	);
	return (
		userCreatedPreLeaderboards &&
		!_.get(notifications, "leaderboards") &&
		canViewLeaderboards
	);
};

const shouldSkipTrainerLeaderboards = function(user, notifications) {
	const canViewLeaderboards = OpsUser.canViewLeaderboards(user);
	const userCreatedAt = _.get(user, "model.createdAt");
	// Ensures the user model is loaded and we have the date when the user was created.
	if (!userCreatedAt) return false;
	const userCreatedPreLeaderboards = isUserBeforeEvent(
		userCreatedAt,
		"parent_leaderboards"
	);
	return (
		!userCreatedPreLeaderboards &&
		!_.get(notifications, "leaderboards") &&
		canViewLeaderboards
	);
};

const shouldSkipReportLeaderboards = function(user, notifications) {
	const userCreatedAt = _.get(user, "model.createdAt");
	// Ensures the user model is loaded and we have the date when the user was created.
	if (!userCreatedAt) return false;
	const userCreatedPreLeaderboards = isUserBeforeEvent(
		userCreatedAt,
		"parent_leaderboards"
	);
	const isParent = OpsUser.hasParentRole(user) && !OpsUser.hasTeacherRole(user);
	return (
		!_.get(notifications, "leaderboards") &&
		!userCreatedPreLeaderboards &&
		isParent
	);
};

const shouldStartBeastbuilder = function(page, userModel, tutorial) {
	const isBBUnlocked =
		!userModel.beastbuilderLockOverride &&
		userModel.beastbuilderProgressUnlocked;
	const isTutorialFinished = _.get(tutorial, "beastbuilder") === "finished";
	return (
		(OpsPage.isInBeastBuilder(page) || OpsPage.isMainProfilePage(page)) &&
		isBBUnlocked &&
		!isTutorialFinished
	);
};

const hasEarnedTrophies = function(userModel) {
	return _.get(userModel, "trophies", 0) > 0;
};

// Users who have never earned a trophy AND
// recently unlocked a trophy problem should see this
const shouldTriggerTrophyModal = function(page, user, demo, notifications) {
	return (
		page.name === "trainer.chapter" &&
		!hasEarnedTrophies(_.get(user, "model")) &&
		_.get(notifications, "trophyUnlocked") === "notShown"
	);
};

// Users should not see this modal if they have ever
// earned a trophy or trophyUnlocked in
// eventsTriggered.notifications isn't "notShown"
const shouldSkipTrophyModal = function(user, notifications) {
	const unlocked = _.get(notifications, "trophyUnlocked");
	return hasEarnedTrophies(_.get(user, "model")) || unlocked;
};

module.exports = {
	triggerStudentNotifications,
	triggerReportNotifications,
	triggerTutorials,
	triggerTeacherWelcome,
	triggerPlacementTestInviteModal,
};
